import {
  DataSourceCycleError,
  type DataSourceDeclaration,
} from '@de/providers/DataSourceManager.types';
import { executionDependencyNames } from '@de/utils/data-sources/readyWhen';

/**
 * A declared source whose `dependsOn` names a source that is not present
 * in the `dataSources` map. Callers (DSM) surface this as
 * `status: 'error'` with {@link DataSourceMissingDependencyError} rather
 * than aborting the whole graph.
 */
export type MissingDependency = {
  readonly source: string;
  readonly missing: string;
};

/**
 * Result of a Kahn topological sort over `dataSources`.
 *
 * - `ordered` — every declared source name, dependencies before dependents
 * - `missingDependencies` — edges skipped because the dependency was not
 *   declared (first missing name per offending source)
 */
export type TopologicalSortResult = {
  readonly ordered: readonly string[];
  readonly missingDependencies: readonly MissingDependency[];
};

/**
 * Topologically order `dataSources` by `dependsOn` using Kahn's algorithm.
 *
 * Returns source names in an order such that every source appears **after**
 * all of its *declared* `dependsOn` entries. Declaration order is preserved
 * among independent sources (deterministic output given the same input).
 *
 * When `dependsOn` names a source that is not declared, that edge is
 * skipped and recorded in {@link TopologicalSortResult.missingDependencies}.
 * The offending source remains in the graph so siblings and other valid
 * edges still order correctly — callers degrade the offender independently
 * (DSM never throws for undeclared deps).
 *
 * Throws {@link DataSourceCycleError} if the declarations form a cycle,
 * including self-references.
 */
export function topologicalSort(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
): TopologicalSortResult {
  const names = Object.keys(dataSources);
  if (names.length === 0) {
    return { ordered: [], missingDependencies: [] };
  }

  const indegree = new Map<string, number>();
  const dependents = new Map<string, string[]>();
  const missingDependencies: MissingDependency[] = [];
  const recordedMissing = new Set<string>();

  for (const name of names) {
    indegree.set(name, 0);
    dependents.set(name, []);
  }

  for (const name of names) {
    const decl = dataSources[name];
    const deps = executionDependencyNames(decl);
    for (const dep of deps) {
      if (!indegree.has(dep)) {
        if (!recordedMissing.has(name)) {
          recordedMissing.add(name);
          missingDependencies.push({ source: name, missing: dep });
        }
        continue;
      }
      indegree.set(name, (indegree.get(name) ?? 0) + 1);
      const list = dependents.get(dep);
      if (list) {
        list.push(name);
      }
    }
  }

  const queue: string[] = [];
  for (const name of names) {
    if ((indegree.get(name) ?? 0) === 0) {
      queue.push(name);
    }
  }

  const ordered: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) {
      break;
    }
    ordered.push(current);
    const children = dependents.get(current) ?? [];
    for (const child of children) {
      const nextDegree = (indegree.get(child) ?? 0) - 1;
      indegree.set(child, nextDegree);
      if (nextDegree === 0) {
        queue.push(child);
      }
    }
  }

  if (ordered.length !== names.length) {
    throw new DataSourceCycleError(extractCycle(dataSources, ordered));
  }

  return { ordered, missingDependencies };
}

/**
 * Walk the remaining dependency graph to recover a concrete cycle for the
 * thrown {@link DataSourceCycleError}. Best-effort — if the cycle spans
 * multiple disjoint components, returns the first we encounter.
 */
function extractCycle(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  resolved: readonly string[],
): string[] {
  const resolvedSet = new Set(resolved);
  const unresolved = Object.keys(dataSources).filter(
    (name) => !resolvedSet.has(name),
  );

  for (const start of unresolved) {
    const path: string[] = [];
    const visited = new Set<string>();
    const cycle = walk(start, dataSources, resolvedSet, path, visited);
    if (cycle !== null) {
      return cycle;
    }
  }

  return unresolved;
}

function walk(
  node: string,
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  resolvedSet: ReadonlySet<string>,
  path: string[],
  visited: Set<string>,
): string[] | null {
  if (path.includes(node)) {
    const start = path.indexOf(node);
    return path.slice(start);
  }
  if (visited.has(node)) {
    return null;
  }
  visited.add(node);
  path.push(node);

  const decl = dataSources[node];
  const deps = executionDependencyNames(decl);
  for (const dep of deps) {
    if (resolvedSet.has(dep)) {
      continue;
    }
    const result = walk(dep, dataSources, resolvedSet, path, visited);
    if (result !== null) {
      return result;
    }
  }

  path.pop();
  return null;
}
